import { Component, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { CartService } from '../../services/cart.service';
import { Constante } from '../../constante';

@Component({
  selector: 'app-cart-drawer',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe
  ],
  template: `
    <!-- Contenedor principal adaptable al 100% en ancho y alto -->
    <div class="w-full h-full bg-white p-3 sm:p-5 rounded-2xl sm:rounded-3xl border border-gray-100 shadow-xl flex flex-col gap-3 sm:gap-4 overflow-hidden box-border">
      
      <!-- TIMER BANNER -->
      <div *ngIf="cartService.cartItems().length > 0 || cartService.isLoading()" class="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl p-2.5 sm:p-4 text-center flex flex-col items-center justify-center shrink-0">
        <span class="text-[11px] sm:text-xs font-semibold text-gray-600">Tiempo restante para completar tu compra:</span>
        <div class="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1 text-rose-500 font-black text-xl sm:text-2xl tracking-wider">
          <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" stroke-width="2" /><path stroke-linecap="round" stroke-width="2" d="M12 7v5l3 2" /></svg>
          <span>{{ cartService.isLoading() ? '...' : cartService.timeRemaining() }}</span>
        </div>
      </div>

      <!-- LOADING -->
      <div *ngIf="cartService.isLoading() && cartService.cartItems().length === 0" class="text-center py-10 text-gray-400 text-sm flex-1 flex items-center justify-center">
        Cargando tu carrito…
      </div>

      <!-- ERROR BACKEND -->
      <div *ngIf="cartService.errorMsg()" class="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 text-center shrink-0">
        <span class="text-xs font-semibold text-red-600">{{ cartService.errorMsg() }}</span>
      </div>

      <!-- LISTA DE PRODUCTOS (Scrolleable y adaptable) -->
      <div class="flex flex-col gap-3 sm:gap-4 flex-1 overflow-y-auto p-1 sm:p-2 overflow-x-hidden">
        
        <div *ngIf="cartService.cartItems().length === 0 && !cartService.isLoading()" class="text-center py-10 text-gray-400 text-sm flex-1 flex items-center justify-center">
          Tu carrito está vacío
        </div>

        <div 
          *ngFor="let item of cartService.cartItems()" 
          class="p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm relative flex flex-col gap-2.5 sm:gap-3 shrink-0"
        >
          <button 
            (click)="cartService.removeItem(item.id)"
            class="absolute -top-2 -right-2 w-7 h-7 bg-rose-500 text-white rounded-full flex items-center justify-center shadow hover:bg-rose-600 transition-all cursor-pointer z-10"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-width="2.5" d="M6 6l12 12M18 6L6 18" /></svg>
          </button>

          <div class="flex gap-2.5 sm:gap-3 items-center">
            <img [src]="item.image" [alt]="item.title" class="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl object-cover shrink-0" />
            
            <div class="flex flex-col gap-0.5 min-w-0 flex-1">
              <h3 class="font-bold text-gray-800 text-xs sm:text-sm leading-snug truncate">{{ item.title }}</h3>
              <div class="flex items-center gap-1.5 sm:gap-2 mt-1">
                <button
                  (click)="cartService.updateQuantity(item.id, item.quantity - 1)"
                  [disabled]="item.quantity <= 1"
                  class="w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-sm font-bold flex items-center justify-center disabled:opacity-40 cursor-pointer"
                >
                  −
                </button>
                <span class="text-[11px] sm:text-xs text-gray-500 font-medium">Cant: × {{ item.quantity }}</span>
                <button
                  (click)="cartService.updateQuantity(item.id, item.quantity + 1)"
                  class="w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-sm font-bold flex items-center justify-center cursor-pointer"
                >
                  +
                </button>
              </div>
              <span class="text-rose-500 font-extrabold text-sm sm:text-base mt-0.5">S/ {{ item.price.toFixed(2) }}</span>
            </div>
          </div>

          <button 
            (click)="toggleDetail(item.id)"
            class="w-full py-1.5 sm:py-2 bg-rose-50 text-rose-500 text-xs font-bold rounded-lg sm:rounded-xl flex items-center justify-center gap-1 cursor-pointer transition-all hover:bg-rose-100"
          >
            <span>Ver detalles</span>
            <svg *ngIf="openDetails()[item.id]" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
            <svg *ngIf="!openDetails()[item.id]" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
          </button>

          <div *ngIf="openDetails()[item.id]" class="bg-gray-50 p-2.5 sm:p-3 rounded-lg sm:rounded-xl flex flex-col gap-1.5 sm:gap-2 text-[11px] sm:text-xs border border-gray-100">
            <div>
              <span class="font-bold text-gray-800 block">Fecha de Entrega:</span>
              <span class="text-gray-600">{{ item.deliveryDate| date:'dd/MM/yyyy' }} ({{ item.deliveryTimeSlot }})</span>
            </div>

            <div *ngIf="item.cardMessage">
              <span class="font-bold text-gray-800 block">Mensaje:</span>
              <span class="text-gray-600 italic">"{{ item.cardMessage }}"</span>
            </div>

            <div *ngIf="item.addons && item.addons.length > 0">
              <span class="font-bold text-gray-800 block mb-1">Complementos:</span>
              <ul class="list-disc list-inside text-gray-600 flex flex-col gap-1">
                <li *ngFor="let addon of item.addons">
                  {{ addon.name }} (+S/ {{ addon.price.toFixed(2) }})
                </li>
              </ul>
            </div>
          </div>

        </div>
      </div>

      <!-- SUBTOTAL Y PAGO (Fijo en el fondo) -->
      <div *ngIf="cartService.cartItems().length > 0" class="flex flex-col gap-2.5 sm:gap-3 pt-2.5 sm:pt-3 border-t border-gray-100 shrink-0">
        <div class="flex justify-between items-baseline">
          <span class="font-bold text-gray-800 text-sm sm:text-base">Subtotal:</span>
          <span class="font-black text-rose-500 text-xl sm:text-2xl">S/ {{ cartService.subtotal().toFixed(2) }}</span>
        </div>

        <button 
          (click)="goToCheckout()"
          class="w-full py-3 sm:py-3.5 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-xl sm:rounded-2xl text-xs sm:text-sm uppercase shadow-md shadow-rose-200 transition-all cursor-pointer"
        >
          Ir a pagar
        </button>
      </div>

    </div>
  `
})
export class CartDrawerComponent {
  cartService = inject(CartService);
  private router = inject(Router);

  openDetails = signal<Record<string, boolean>>({});

  toggleDetail(itemId: string) {
    this.openDetails.update(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  }

  goToCheckout() {
    this.router.navigate([Constante.URL_CHECKOUT]);
  }
}